"use client";

import React, { useState, useCallback, useRef, useEffect } from "react";
import { Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { Constants } from "src/utils/constants";
import { Building2, MapPin, X } from "lucide-react";
import LoadingSpinner from "./LoadingSpinner";

// Helper to extract image URLs from space object
const extractImageUrls = space => {
  if (space?.media?.images && Array.isArray(space.media.images) && space.media.images.length > 0) {
    return space.media.images.map(img => (typeof img === "string" ? img : img.url)).filter(Boolean);
  }
  if (space?.images && Array.isArray(space.images) && space.images.length > 0) {
    return space.images.map(img => (typeof img === "string" ? img : img.url || img)).filter(Boolean);
  }
  if (space?.image) {
    return [space.image];
  }
  return ["/img/placeholder-space.jpg"];
};

// SpaceImageCarousel for map popup
const SpaceImageCarousel = React.memo(
  ({ space, className = "", autoCycle = true, isHovered = false }) => {
    const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
    const [imageErrors, setImageErrors] = React.useState(new Set());

    const imageUrlsKey = extractImageUrls(space).join("|");
    const images = React.useMemo(() => imageUrlsKey.split("|"), [imageUrlsKey]);

    React.useEffect(() => {
      if (!autoCycle || !isHovered || images.length <= 1) return;
      const randomInterval = Math.random() * 2000 + 5000;
      const interval = setInterval(() => {
        setCurrentImageIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
      }, randomInterval);
      return () => clearInterval(interval);
    }, [images.length, autoCycle, isHovered]);

    if (images.length <= 1) {
      const imgSrc = images[0];
      const isPlaceholder = imgSrc === "/img/placeholder-space.jpg";
      return (
        <img
          src={imgSrc}
          alt={space.name}
          className={`w-full h-full object-cover rounded-lg ${className}`}
          onError={e => {
            if (!isPlaceholder && e.target.src !== "/img/placeholder-space.jpg") {
              e.target.src = "/img/placeholder-space.jpg";
            }
          }}
        />
      );
    }

    const currentImage = images[currentImageIndex];
    const hasError = imageErrors.has(currentImageIndex);

    return (
      <div className={`relative w-full h-full overflow-hidden rounded-lg ${className}`}>
        <img
          src={hasError ? "/img/placeholder-space.jpg" : currentImage}
          alt={`${space.name} - Image ${currentImageIndex + 1}`}
          className="w-full h-full object-cover transition-opacity duration-300"
          onError={() => setImageErrors(prev => new Set([...prev, currentImageIndex]))}
        />
        {images.length > 1 && (
          <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-md font-medium">
            {currentImageIndex + 1} / {images.length}
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    const prevUrls = extractImageUrls(prevProps.space).join("|");
    const nextUrls = extractImageUrls(nextProps.space).join("|");
    return (
      prevUrls === nextUrls &&
      prevProps.className === nextProps.className &&
      prevProps.autoCycle === nextProps.autoCycle &&
      prevProps.isHovered === nextProps.isHovered
    );
  }
);

// MapMarker content (rendered inside AdvancedMarker)
const MapMarkerContent = React.memo(({ space, onClick, animationDelay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), animationDelay);
    return () => clearTimeout(timer);
  }, [animationDelay]);

  let markerColor = Constants.HOST_EXPERIENCE_BASE_COLOR;
  if (space.status === "Booked") markerColor = "#F44336";

  return (
    <div
      className={`cursor-pointer transition-all duration-300 ${isHovered ? "z-10 scale-110" : ""} ${isVisible ? "opacity-100 scale-100" : "opacity-0 scale-50"}`}
      style={{ animation: isVisible ? "popIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)" : "none" }}
      onClick={() => onClick(space)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className="px-2 py-1 mb-1 text-sm font-bold text-center text-white whitespace-nowrap rounded shadow-lg"
        style={{ backgroundColor: markerColor }}
      >
        {space.pricing}
      </div>
      <div
        className="mx-auto w-0 h-0"
        style={{
          borderLeft: "6px solid transparent",
          borderRight: "6px solid transparent",
          borderTop: `6px solid ${markerColor}`,
        }}
      />
      <div
        className="mx-auto mt-1 w-4 h-4 rounded-full border-2 border-white shadow-lg"
        style={{ backgroundColor: markerColor }}
      />
    </div>
  );
});

/**
 * Inner component using useMap() hook for programmatic control.
 * Must be a child of <Map>.
 */
const MapEffects = ({ spaces, focusedSpace, onMapReady, showQuickView, onQuickViewChange }) => {
  const map = useMap();
  const lastFocusedIdRef = useRef(null);

  const fitMapToSpaces = useCallback(() => {
    if (!map || !spaces.length) return;

    if (spaces.length === 1) {
      map.setCenter({ lat: spaces[0].coordinates.latitude, lng: spaces[0].coordinates.longitude });
      map.setZoom(14);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    spaces.forEach(space => {
      const lat = parseFloat(space.coordinates.latitude);
      const lng = parseFloat(space.coordinates.longitude);
      if (!isNaN(lat) && !isNaN(lng)) bounds.extend(new window.google.maps.LatLng(lat, lng));
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
      window.google.maps.event.addListenerOnce(map, "idle", () => {
        map.setZoom(Math.max(map.getZoom() - 1, 3));
      });
    }
  }, [map, spaces]);

  // Notify parent that map is ready with fitMapToSpaces callback
  useEffect(() => {
    if (map && onMapReady) {
      onMapReady({ fitMapToSpaces });
      fitMapToSpaces();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Re-fit when spaces change
  useEffect(() => {
    if (map && spaces.length) {
      fitMapToSpaces();
    }
  }, [map, fitMapToSpaces, spaces]);

  // Focus on a specific space
  useEffect(() => {
    if (!map || !focusedSpace) return;

    const coords = focusedSpace.coordinates;
    if (!coords) return;

    const lat =
      typeof coords.latitude !== "undefined"
        ? parseFloat(coords.latitude)
        : Array.isArray(coords)
          ? parseFloat(coords[1])
          : parseFloat(coords.lat);
    const lng =
      typeof coords.longitude !== "undefined"
        ? parseFloat(coords.longitude)
        : Array.isArray(coords)
          ? parseFloat(coords[0])
          : parseFloat(coords.lng);

    if (isNaN(lat) || isNaN(lng)) return;

    const currentZoom = map.getZoom();
    const targetZoom = 16;

    if (lastFocusedIdRef.current === focusedSpace.id) {
      map.setZoom(currentZoom - 2);
      setTimeout(() => {
        if (map) {
          map.panTo({ lat, lng });
          map.setZoom(targetZoom);
        }
      }, 200);
    } else {
      map.panTo({ lat, lng });
      if (currentZoom !== targetZoom) map.setZoom(targetZoom);
    }

    lastFocusedIdRef.current = focusedSpace.id;

    if (showQuickView) {
      onQuickViewChange(focusedSpace);
    }
  }, [map, focusedSpace, showQuickView, onQuickViewChange]);

  return null;
};

/**
 * Helper function to normalize coordinates from various formats
 */
const normalizeCoordinates = coords => {
  if (!coords) return null;

  let lat = null;
  let lng = null;

  if (Array.isArray(coords)) {
    if (coords.length >= 2) {
      lng = parseFloat(coords[0]);
      lat = parseFloat(coords[1]);
    }
  } else if (typeof coords === "object") {
    if (coords.latitude !== undefined && coords.longitude !== undefined) {
      lat = parseFloat(coords.latitude);
      lng = parseFloat(coords.longitude);
    } else if (coords.lat !== undefined && coords.lng !== undefined) {
      lat = parseFloat(coords.lat);
      lng = parseFloat(coords.lng);
    }
  }

  if (isNaN(lat) || isNaN(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { latitude: lat, longitude: lng };
};

/**
 * Compute default center and zoom from a list of spaces
 */
const computeMapView = spaces => {
  if (!spaces.length) return { center: { lat: 37.7749, lng: -122.4194 }, zoom: 13 };

  if (spaces.length === 1) {
    return {
      center: { lat: parseFloat(spaces[0].coordinates.latitude), lng: parseFloat(spaces[0].coordinates.longitude) },
      zoom: 14,
    };
  }

  let minLat = Infinity,
    maxLat = -Infinity,
    minLng = Infinity,
    maxLng = -Infinity;
  spaces.forEach(s => {
    const lat = parseFloat(s.coordinates.latitude);
    const lng = parseFloat(s.coordinates.longitude);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  });

  const center = { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
  const maxDiff = Math.max(maxLat - minLat, maxLng - minLng);
  let zoom = 13;
  if (maxDiff < 0.01) zoom = 15;
  else if (maxDiff < 0.05) zoom = 13;
  else if (maxDiff < 0.1) zoom = 11;
  else if (maxDiff < 0.5) zoom = 9;
  else if (maxDiff < 2) zoom = 7;
  else zoom = 6;

  return { center, zoom };
};

/**
 * ReusableMapView Component
 */
const ReusableMapView = ({
  spaces = [],
  onSpaceClick,
  className = "",
  style = {},
  _showLegend = true,
  showQuickView = true,
  focusedSpace = null,
  onClearFocus,
}) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapQuickView, setMapQuickView] = useState(null);
  const mapActionsRef = useRef(null);

  const handleMarkerClick = useCallback(
    space => {
      if (showQuickView) {
        setMapQuickView(space);
      } else if (onSpaceClick) {
        onSpaceClick(space);
      }
    },
    [onSpaceClick, showQuickView]
  );

  const closeQuickView = useCallback(() => setMapQuickView(null), []);

  const handleMapReady = useCallback(({ fitMapToSpaces }) => {
    mapActionsRef.current = { fitMapToSpaces };
    setMapLoaded(true);
  }, []);

  // Normalize coordinates for all spaces and filter out invalid ones
  const spacesWithCoords = React.useMemo(() => {
    return spaces
      .map(space => {
        const normalized = normalizeCoordinates(space.coordinates);
        if (normalized) return { ...space, coordinates: normalized };
        return null;
      })
      .filter(Boolean);
  }, [spaces]);

  const { center, zoom } = React.useMemo(() => computeMapView(spacesWithCoords), [spacesWithCoords]);

  // Early return if no spaces with valid coordinates
  if (spacesWithCoords.length === 0) {
    return (
      <div
        className={`w-full h-full bg-white rounded-2xl border border-gray-200 shadow-lg flex items-center justify-center ${className}`}
        style={style}
      >
        <div className="text-center">
          <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Map View</h3>
          <p className="text-sm text-gray-500">
            {spaces?.length || 0} {spaces?.length === 1 ? "space" : "spaces"} found
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`w-full h-full bg-gray-50 rounded-2xl overflow-hidden shadow-lg relative ${className}`}
      style={style}
    >
      {!mapLoaded && (
        <div className="flex absolute inset-0 z-10 justify-center items-center bg-gray-100 rounded-2xl">
          <LoadingSpinner loading={true} message="Loading map view..." padding="py-8" className="text-blue-600" />
        </div>
      )}

      <Map
        mapId="DEMO_MAP_ID"
        defaultCenter={center}
        defaultZoom={zoom}
        gestureHandling="greedy"
        disableDefaultUI={false}
        mapTypeControl={false}
        zoomControl={true}
        fullscreenControl={false}
        styles={[
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9e2f6" }] },
          { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f5f4f0" }] },
          { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#e8ede0" }] },
          { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#d2e6c8" }] },
          { featureType: "road.highway", elementType: "geometry.fill", stylers: [{ color: "#ffffff" }] },
          { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#e3e3e3" }] },
          { featureType: "road.arterial", elementType: "geometry.fill", stylers: [{ color: "#ffffff" }] },
          { featureType: "road.local", elementType: "geometry.fill", stylers: [{ color: "#ffffff" }] },
          { featureType: "transit", elementType: "geometry", stylers: [{ color: "#e5e5e5" }] },
          { featureType: "administrative", elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
          { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
        ]}
        onClick={() => setMapQuickView(null)}
        style={{ width: "100%", height: "100%" }}
      >
        <MapEffects
          spaces={spacesWithCoords}
          focusedSpace={focusedSpace}
          onMapReady={handleMapReady}
          showQuickView={showQuickView}
          onQuickViewChange={setMapQuickView}
        />

        {spacesWithCoords.map((space, index) => (
          <AdvancedMarker
            key={space.id}
            position={{ lat: space.coordinates.latitude, lng: space.coordinates.longitude }}
            onClick={() => handleMarkerClick(space)}
          >
            <MapMarkerContent space={space} onClick={handleMarkerClick} animationDelay={index * 80} />
          </AdvancedMarker>
        ))}
      </Map>

      {/* Clear Focus Button */}
      {focusedSpace && onClearFocus && (
        <button
          onClick={() => {
            setMapQuickView(null);
            onClearFocus();
            mapActionsRef.current?.fitMapToSpaces();
          }}
          className="absolute top-4 left-4 z-20 flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <X className="w-4 h-4" />
          Clear selection
        </button>
      )}

      {showQuickView && mapQuickView && (
        <div className="absolute bottom-4 left-4 z-20 p-6 w-96 bg-white rounded-xl shadow-2xl border border-gray-200">
          <button
            onClick={closeQuickView}
            className="absolute top-3 right-3 text-xl text-gray-400 hover:text-gray-600 transition-colors"
          >
            ×
          </button>
          <div className="flex mb-4">
            <div className="flex-shrink-0 mr-4 w-24 h-24">
              <SpaceImageCarousel space={mapQuickView} className="w-full h-full" autoCycle={true} isHovered={true} />
            </div>
            <div className="flex-1">
              <h6 className="mb-2 text-lg font-bold text-gray-900 leading-tight">{mapQuickView.name}</h6>
              <p className="mb-3 text-sm text-gray-600">{mapQuickView.location || "No address"}</p>
              <div className="flex items-center gap-3 mb-2">
                <span
                  className={`text-xs font-semibold px-3 py-1 rounded-full ${mapQuickView.status === "PUBLISHED" ? "bg-green-100 text-green-800" : "bg-orange-100 text-orange-800"}`}
                >
                  {mapQuickView.status === "PUBLISHED" ? "Available" : "Unpublished"}
                </span>
                <div className="flex items-center gap-1">
                  <Building2 className="w-3 h-3 text-blue-500" />
                  <span className="text-xs text-gray-600">
                    {mapQuickView.instanceCount || mapQuickView.instances?.length || 0} instances
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-100 pt-4">
            <div className="flex justify-between items-center mb-3">
              <div>
                <div className="text-2xl font-bold text-gray-900">{mapQuickView.pricing}</div>
                <div className="text-sm text-gray-500">per hour</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold text-gray-900">{mapQuickView.capacity}</div>
                <div className="text-sm text-gray-500">people</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (onSpaceClick) onSpaceClick(mapQuickView);
                  closeQuickView();
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
              >
                View Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReusableMapView;
